import { localTimeToUtcIso } from "./marine";
import { calibratedSurfFaceHeight } from "./nearshore";
import type { BreakCalibration } from "./regions";

/* ---------- Types ---------- */

export type ForecastVariant =
  | "champion"
  | "raw_model"
  | "bias_corrected_equal"
  | "weighted_ensemble"
  | "spot_calibrated";

export type ForecastModelSlug = "ecmwf_wam" | "ncep_gfswave025" | "meteofrance_wave";
export const FORECAST_MODEL_SLUGS = [
  "ecmwf_wam",
  "ncep_gfswave025",
  "meteofrance_wave",
] as const satisfies readonly ForecastModelSlug[];

export type TideTrend = "rising" | "falling" | "slack";

export type ForecastBreakType = "beach" | "reef" | "point";

export interface ForecastSnapshot {
  schemaVersion?: 2;
  variant?: ForecastVariant;
  model?: ForecastModelSlug;
  regionId: string;
  /** Stable break-local id when the forecast targets a specific break. */
  breakId?: string;
  /**
   * Coordinate the forecast was sampled at. For a 0h nowcast row this
   * is the buoy's location (so forecast and observation are compared at
   * the same point); for longer-lead forecast-only rows this is the
   * user's target break.
   */
  lat: number;
  lon: number;
  /** ISO timestamp when the forecast was issued / logged ("now"). */
  timestamp: string;
  /**
   * ISO timestamp the forecast is predicting for. For a nowcast, equal
   * to `timestamp`. For future hours, further ahead. Optional for
   * backward compatibility with pre-valid-time snapshots.
   */
  validTime?: string;
  /**
   * Integer hours from `timestamp` to `validTime`. 0 for nowcast, 24 for
   * tomorrow-same-hour, etc. Optional for backward compat.
   */
  leadTimeHours?: number;
  forecastWaveHeightM: number;
  /**
   * Dominant forecast period (s). Optional because Open-Meteo's per-model
   * output can drop to null at domain edges; when it's missing we store
   * nothing so downstream MAE math can skip the row instead of treating
   * `0` as a real forecast. Old rows written before this change carry a
   * literal `0` in the DB — the aggregator filters those out too.
   */
  forecastPeriodS?: number;
  forecastWaveDirectionDeg?: number;
  forecastSwellHeightM?: number;
  forecastSwellPeriodS?: number;
  forecastSwellDirectionDeg?: number;
  forecastWindSpeedKmh: number;
  forecastWindDirectionDeg?: number;
  forecastWindGustKmh?: number;
  forecastTideHeightM?: number;
  forecastTideTrend?: TideTrend;
  shoreNormal?: number;
  breakType?: ForecastBreakType;
  confidence?: number;
  hasObservation?: boolean;
  /** Present only when this row pairs a live buoy reading with the forecast. */
  observedWaveHeightM?: number;
  observedPeriodS?: number;
  observedWaveDirectionDeg?: number;
  buoyStationId?: string;
  /**
   * Buoy coordinate where the observation was taken. When set, `lat` /
   * `lon` should equal these values — meaning the forecast was
   * resampled at the buoy so forecast and observation are
   * apples-to-apples.
   */
  observedLat?: number;
  observedLon?: number;
  /**
   * Haversine distance (km) between the user's target (region) and the
   * buoy that supplied the observation. Lets downstream filter to
   * "observations within X km of the user's spot" when computing MAE.
   */
  observationDistanceKm?: number;
  /**
   * Static/current distance to the nearest buoy used for confidence on future rows.
   * Unlike observationDistanceKm, this can be present before a row is backfilled.
   */
  nearestBuoyDistanceKm?: number;
}

/**
 * Accuracy figures for one region / one lead-time bucket.
 *
 * A "nowcast" row (leadTimeHours = 0) carries both forecast and
 * observation at the same moment. Pure-forecast rows for longer lead
 * times lack an observation until a future job back-fills them, so
 * `sampleCount` at 24h/48h/72h will be 0 until that pipeline exists —
 * the bucket is still returned so the dashboard can show "not measured
 * yet" instead of silently hiding it.
 */
export interface AccuracyStats {
  regionId: string;
  sampleCount: number;
  waveHeightMAE: number | null;
  periodMAE: number | null;
  bias: number | null;
  lastUpdated: string;
}

export interface RegionAccuracy {
  regionId: string;
  overall: AccuracyStats;
  byLeadTime: Record<string, AccuracyStats>;
}

/** Lead-time bucket labels the GET endpoint returns stats for. */
export const LEAD_TIME_BUCKETS = [0, 6, 24, 48, 72] as const;
export type LeadTimeBucket = (typeof LEAD_TIME_BUCKETS)[number];

/* ---------- Client → API ---------- */

const ENDPOINT = "/api/forecast-accuracy";

/**
 * Log one or more forecast snapshots. Accepts either a single snapshot
 * (legacy shape) or a batch; fire-and-forget and error-swallowed so
 * telemetry can never break the UI.
 */
export async function logForecastSnapshot(
  snapshot: ForecastSnapshot | ForecastSnapshot[],
): Promise<void> {
  const body = Array.isArray(snapshot) ? { snapshots: snapshot } : snapshot;
  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Silently fail — accuracy tracking should never break the app
  }
}

/** Fetch accuracy stats for a single region, grouped by lead-time bucket. */
export async function getRegionAccuracy(regionId: string): Promise<RegionAccuracy> {
  try {
    const res = await fetch(`${ENDPOINT}?regionId=${encodeURIComponent(regionId)}`);
    if (!res.ok) return emptyRegionAccuracy(regionId);
    const data = (await res.json()) as { stats?: RegionAccuracy[] };
    return data.stats?.[0] ?? emptyRegionAccuracy(regionId);
  } catch {
    return emptyRegionAccuracy(regionId);
  }
}

/**
 * Fetch accuracy stats for every region that has data. Used by the
 * dashboard; returns an empty array on network / server failure rather
 * than throwing, since "no data" and "fetch failed" render the same way
 * for a monitoring view.
 */
export async function getAllAccuracy(): Promise<RegionAccuracy[]> {
  try {
    const res = await fetch(ENDPOINT);
    if (!res.ok) return [];
    const data = (await res.json()) as { stats?: RegionAccuracy[] };
    return data.stats ?? [];
  } catch {
    return [];
  }
}

function emptyStats(regionId: string): AccuracyStats {
  return {
    regionId,
    sampleCount: 0,
    waveHeightMAE: null,
    periodMAE: null,
    bias: null,
    lastUpdated: new Date().toISOString(),
  };
}

function emptyRegionAccuracy(regionId: string): RegionAccuracy {
  const byLeadTime: Record<string, AccuracyStats> = {};
  for (const b of LEAD_TIME_BUCKETS) {
    byLeadTime[String(b)] = emptyStats(regionId);
  }
  return {
    regionId,
    overall: emptyStats(regionId),
    byLeadTime,
  };
}

/* ---------- Helpers for building a batch ---------- */

/**
 * Bucket a lead-time in hours into the nearest canonical bucket. Values
 * within ±2h of a bucket are rounded to that bucket; everything else
 * rounds to the closest. This keeps aggregation stable even when the
 * upstream hourly-forecast grid drifts by a minute or two.
 */
export function roundToLeadTimeBucket(hours: number): LeadTimeBucket {
  let best: LeadTimeBucket = LEAD_TIME_BUCKETS[0];
  let bestDiff = Math.abs(hours - best);
  for (const b of LEAD_TIME_BUCKETS) {
    const d = Math.abs(hours - b);
    if (d < bestDiff) {
      best = b;
      bestDiff = d;
    }
  }
  return best;
}

/** Whole hours between two ISO strings (issueTime → validTime). */
export function leadTimeHoursBetween(issueTime: string, validTime: string): number {
  const diffMs = new Date(validTime).getTime() - new Date(issueTime).getTime();
  return Math.max(0, Math.round(diffMs / (60 * 60 * 1000)));
}

/**
 * Find the hourly entry closest to `issueTime + targetLeadHours`.
 *
 * Open-Meteo's marine endpoint returns the hourly array starting at
 * 00:00 local time of the first forecast day, not from "now" — so
 * indexing `hourly[targetLeadHours]` does NOT give you a forecast
 * `targetLeadHours` hours ahead. Callers must search by time.
 *
 * Returns `{index, validTime, actualLeadHours}` where `actualLeadHours`
 * is the rounded whole-hour gap between issueTime and the matched
 * validTime (may differ from `targetLeadHours` if the forecast window
 * doesn't quite cover it). Returns `null` for an empty array, if the
 * nearest entry is in the past, or if the gap is more than
 * `toleranceHours` from the target (default 2h — enough to absorb
 * half-hour grid offsets without accepting a totally wrong row).
 */
export function findForecastAtLead(
  hourlyTimes: readonly string[],
  issueTime: string,
  targetLeadHours: number,
  toleranceHours: number = 2,
): { index: number; validTime: string; actualLeadHours: number } | null {
  if (hourlyTimes.length === 0) return null;
  const issueMs = new Date(issueTime).getTime();
  const targetMs = issueMs + targetLeadHours * 60 * 60 * 1000;
  let bestIdx = -1;
  let bestDiff = Infinity;
  for (let i = 0; i < hourlyTimes.length; i++) {
    const d = Math.abs(new Date(hourlyTimes[i]).getTime() - targetMs);
    if (d < bestDiff) {
      bestDiff = d;
      bestIdx = i;
    }
  }
  if (bestIdx < 0) return null;
  if (bestDiff > toleranceHours * 60 * 60 * 1000) return null;
  const validTimeMs = new Date(hourlyTimes[bestIdx]).getTime();
  const deltaHours = Math.round((validTimeMs - issueMs) / (60 * 60 * 1000));
  if (deltaHours < 0) return null;
  return {
    index: bestIdx,
    validTime: hourlyTimes[bestIdx],
    // Math.round can return -0 when the nearest grid slot is slightly
    // before issueTime. Normalize to +0 so downstream bucket lookups
    // and Object.is comparisons behave.
    actualLeadHours: Math.max(0, deltaHours),
  };
}

/**
 * Haversine distance between two (lat, lon) points in kilometers.
 * Used to record how far a buoy observation sits from the user's target
 * break so downstream analysis can filter or weight accordingly.
 */
export function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371; // Earth radius, km
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/* ---------- Snapshot batch builder ---------- */

export interface AccuracyMarineSource {
  /**
   * Spot's UTC offset (seconds) for this source's hourly time strings.
   * Open-Meteo hourly times are spot-local with no offset designator;
   * when this is present the builder converts them to true UTC before
   * lead matching and emits `Z`-suffixed `validTime`s. When absent
   * (cached pre-offset responses), times are compared as before —
   * correct only when the viewer's timezone matches the spot's.
   */
  utcOffsetSeconds?: number | null;
  offshoreHeightM: number | null;
  wavePeriod: number | null;
  waveDirection?: number | null;
  swellHeightM?: number | null;
  swellPeriod?: number | null;
  swellDirection?: number | null;
  hourly: Array<{
    time: string;
    offshoreHeightM: number | null;
    wavePeriod: number | null;
    waveDirection?: number | null;
    swellHeightM?: number | null;
    swellPeriod?: number | null;
    swellDirection?: number | null;
  }>;
}

export type AccuracyRawModelSources = Partial<
  Record<ForecastModelSlug, AccuracyMarineSource>
>;

export interface AccuracyBuoyStation {
  id: string;
  lat: number;
  lon: number;
}

export interface AccuracyBuoyReading {
  waveHeight: number | null; // feet, from client NDBC parser
  dominantPeriod: number | null;
}

export interface AccuracyTideSample {
  heightM: number;
  trend: TideTrend;
}

export interface BuildForecastSnapshotsInput {
  regionId: string;
  targetLat: number;
  targetLon: number;
  issueTime: string;
  marine: AccuracyMarineSource;
  forecastWindSpeedKmh: number;
  forecastWindDirectionDeg?: number | null;
  forecastWindGustKmh?: number | null;
  breakId?: string | null;
  shoreNormal?: number | null;
  breakType?: ForecastBreakType | null;
  buoyStation?: AccuracyBuoyStation | null;
  buoyMarine?: AccuracyMarineSource | null;
  rawModels?: AccuracyRawModelSources | null;
  buoyRawModels?: AccuracyRawModelSources | null;
  buoyReading?: AccuracyBuoyReading | null;
  includeRawModelSnapshots?: boolean;
  includeSpotCalibratedSnapshots?: boolean;
  spotCalibration?: BreakCalibration | null;
  forecastTideAt?: (validTime: string) => AccuracyTideSample | null;
  leadBuckets?: readonly LeadTimeBucket[];
}

interface ForecastValuesAtLead {
  offshoreM: number;
  periodS?: number | null;
  waveDirectionDeg?: number | null;
  swellHeightM?: number | null;
  swellPeriodS?: number | null;
  swellDirectionDeg?: number | null;
  validTime: string;
  actualLeadHours: number;
}

function readForecastValuesAtLead(
  src: AccuracyMarineSource,
  issueTime: string,
  leadH: number,
): ForecastValuesAtLead | null {
  const offset = src.utcOffsetSeconds;
  const hourlyTimes =
    src.hourly?.map((h) =>
      offset != null && Number.isFinite(offset)
        ? localTimeToUtcIso(h.time, offset)
        : h.time,
    ) ?? [];

  if (leadH === 0) {
    if (src.offshoreHeightM == null) return null;
    return {
      offshoreM: src.offshoreHeightM,
      periodS: src.wavePeriod,
      waveDirectionDeg: src.waveDirection,
      swellHeightM: src.swellHeightM,
      swellPeriodS: src.swellPeriod,
      swellDirectionDeg: src.swellDirection,
      validTime: issueTime,
      actualLeadHours: 0,
    };
  }

  const match = findForecastAtLead(hourlyTimes, issueTime, leadH);
  if (!match) return null;
  const entry = src.hourly?.[match.index];
  if (!entry || entry.offshoreHeightM == null) return null;

  return {
    offshoreM: entry.offshoreHeightM,
    periodS: entry.wavePeriod,
    waveDirectionDeg: entry.waveDirection,
    swellHeightM: entry.swellHeightM,
    swellPeriodS: entry.swellPeriod,
    swellDirectionDeg: entry.swellDirection,
    validTime: match.validTime,
    actualLeadHours: match.actualLeadHours,
  };
}

interface MakeSnapshotInput {
  variant: ForecastVariant;
  model?: ForecastModelSlug;
  values: ForecastValuesAtLead;
  regionId: string;
  breakId?: string | null;
  sourceLat: number;
  sourceLon: number;
  issueTime: string;
  forecastWindSpeedKmh: number;
  forecastWindDirectionDeg?: number | null;
  forecastWindGustKmh?: number | null;
  shoreNormal?: number | null;
  breakType?: ForecastBreakType | null;
  buoyStation?: AccuracyBuoyStation | null;
  observationDistanceKm?: number | null;
  buoyReading?: AccuracyBuoyReading | null;
  forecastTide?: AccuracyTideSample | null;
}

function makeForecastSnapshot({
  variant,
  model,
  values,
  regionId,
  breakId,
  sourceLat,
  sourceLon,
  issueTime,
  forecastWindSpeedKmh,
  forecastWindDirectionDeg,
  forecastWindGustKmh,
  shoreNormal,
  breakType,
  buoyStation,
  observationDistanceKm,
  buoyReading,
  forecastTide,
}: MakeSnapshotInput): ForecastSnapshot {
  const snapshot: ForecastSnapshot = {
    schemaVersion: 2,
    variant,
    regionId,
    lat: sourceLat,
    lon: sourceLon,
    timestamp: issueTime,
    validTime: values.validTime,
    leadTimeHours: values.actualLeadHours,
    forecastWaveHeightM: values.offshoreM,
    forecastWindSpeedKmh,
    hasObservation: false,
  };

  if (model) snapshot.model = model;
  if (breakId) snapshot.breakId = breakId;
  if (forecastWindDirectionDeg != null) {
    snapshot.forecastWindDirectionDeg = forecastWindDirectionDeg;
  }
  if (forecastWindGustKmh != null) {
    snapshot.forecastWindGustKmh = forecastWindGustKmh;
  }
  if (shoreNormal != null) snapshot.shoreNormal = shoreNormal;
  if (breakType != null) snapshot.breakType = breakType;
  if (values.periodS != null && values.periodS > 0) {
    snapshot.forecastPeriodS = values.periodS;
  }
  if (values.waveDirectionDeg != null) {
    snapshot.forecastWaveDirectionDeg = values.waveDirectionDeg;
  }
  if (values.swellHeightM != null) {
    snapshot.forecastSwellHeightM = values.swellHeightM;
  }
  if (values.swellPeriodS != null && values.swellPeriodS > 0) {
    snapshot.forecastSwellPeriodS = values.swellPeriodS;
  }
  if (values.swellDirectionDeg != null) {
    snapshot.forecastSwellDirectionDeg = values.swellDirectionDeg;
  }
  if (forecastTide) {
    snapshot.forecastTideHeightM = forecastTide.heightM;
    snapshot.forecastTideTrend = forecastTide.trend;
  }

  if (buoyStation) {
    snapshot.buoyStationId = buoyStation.id;
    snapshot.observedLat = buoyStation.lat;
    snapshot.observedLon = buoyStation.lon;
    if (observationDistanceKm != null) {
      snapshot.observationDistanceKm = observationDistanceKm;
      snapshot.nearestBuoyDistanceKm = observationDistanceKm;
    }

    if (values.actualLeadHours === 0 && buoyReading?.waveHeight != null) {
      snapshot.observedWaveHeightM = buoyReading.waveHeight / 3.28084;
      snapshot.hasObservation = true;
      if (buoyReading.dominantPeriod != null) {
        snapshot.observedPeriodS = buoyReading.dominantPeriod;
      }
    }
  }

  return snapshot;
}

function spotCalibratedValues(
  values: ForecastValuesAtLead,
  options: {
    breakType?: ForecastBreakType | null;
    shoreNormal?: number | null;
    calibration?: BreakCalibration | null;
    tide?: AccuracyTideSample | null;
  },
): ForecastValuesAtLead {
  return {
    ...values,
    offshoreM: calibratedSurfFaceHeight({
      offshoreHeightM: values.offshoreM,
      periodS: values.periodS,
      breakType: options.breakType ?? "beach",
      swellDirectionDeg: values.swellDirectionDeg ?? values.waveDirectionDeg,
      shoreNormal: options.shoreNormal,
      tideHeightM: options.tide?.heightM,
      tideTrend: options.tide?.trend,
      calibration: options.calibration,
    }),
  };
}

/**
 * Build one accuracy row per lead bucket.
 *
 * When a buoy station is available, forecasts are sampled at the buoy
 * coordinate for every bucket so future rows can later be compared to
 * the same buoy by the back-fill cron. The UI forecast may use the break
 * coordinate, but telemetry should avoid mixing model error with the
 * region-to-buoy spatial offset.
 */
export function buildForecastSnapshots({
  regionId,
  targetLat,
  targetLon,
  issueTime,
  marine,
  forecastWindSpeedKmh,
  forecastWindDirectionDeg = null,
  forecastWindGustKmh = null,
  breakId = null,
  shoreNormal = null,
  breakType = null,
  buoyStation = null,
  buoyMarine = null,
  rawModels = null,
  buoyRawModels = null,
  buoyReading = null,
  includeRawModelSnapshots = false,
  includeSpotCalibratedSnapshots = false,
  spotCalibration = null,
  forecastTideAt,
  leadBuckets = LEAD_TIME_BUCKETS,
}: BuildForecastSnapshotsInput): ForecastSnapshot[] {
  const useBuoySource = buoyStation != null && buoyMarine != null;
  const src = useBuoySource ? buoyMarine : marine;
  const rawModelSources = includeRawModelSnapshots
    ? useBuoySource
      ? buoyRawModels
      : rawModels
    : null;
  const sourceLat = useBuoySource ? buoyStation.lat : targetLat;
  const sourceLon = useBuoySource ? buoyStation.lon : targetLon;
  const observationDistanceKm = useBuoySource
    ? Math.round(
        haversineKm(targetLat, targetLon, buoyStation.lat, buoyStation.lon) * 10,
      ) / 10
    : null;

  const snapshots: ForecastSnapshot[] = [];

  for (const leadH of leadBuckets) {
    const championValues = readForecastValuesAtLead(src, issueTime, leadH);
    if (!championValues) continue;
    const championTide = forecastTideAt?.(championValues.validTime) ?? null;

    snapshots.push(
      makeForecastSnapshot({
        variant: "champion",
        values: championValues,
        regionId,
        breakId,
        sourceLat,
        sourceLon,
        issueTime,
        forecastWindSpeedKmh,
        forecastWindDirectionDeg,
        forecastWindGustKmh,
        shoreNormal,
        breakType,
        buoyStation: useBuoySource ? buoyStation : null,
        observationDistanceKm,
        buoyReading,
        forecastTide: championTide,
      }),
    );

    if (includeSpotCalibratedSnapshots) {
      const spotValues = useBuoySource
        ? readForecastValuesAtLead(marine, issueTime, leadH)
        : championValues;
      if (spotValues) {
        const spotTide = forecastTideAt?.(spotValues.validTime) ?? null;
        snapshots.push(
          makeForecastSnapshot({
            variant: "spot_calibrated",
            values: spotCalibratedValues(spotValues, {
              breakType,
              shoreNormal,
              calibration: spotCalibration,
              tide: spotTide,
            }),
            regionId,
            breakId,
            sourceLat: targetLat,
            sourceLon: targetLon,
            issueTime,
            forecastWindSpeedKmh,
            forecastWindDirectionDeg,
            forecastWindGustKmh,
            shoreNormal,
            breakType,
            forecastTide: spotTide,
          }),
        );
      }
    }

    if (!rawModelSources) continue;
    for (const model of FORECAST_MODEL_SLUGS) {
      const modelSource = rawModelSources[model];
      if (!modelSource) continue;
      const modelValues = readForecastValuesAtLead(modelSource, issueTime, leadH);
      if (!modelValues) continue;
      snapshots.push(
        makeForecastSnapshot({
          variant: "raw_model",
          model,
          values: modelValues,
          regionId,
          breakId,
          sourceLat,
          sourceLon,
          issueTime,
          forecastWindSpeedKmh,
          forecastWindDirectionDeg,
          forecastWindGustKmh,
          shoreNormal,
          breakType,
          buoyStation: useBuoySource ? buoyStation : null,
          observationDistanceKm,
          buoyReading,
          forecastTide: championTide,
        }),
      );
    }
  }

  return snapshots;
}
